// src/pages/communication/ChatPage.jsx
// npm install socket.io-client

import React, { useState, useRef, useEffect, useCallback } from 'react';
import useChat from '../../hooks/useChat';
import authService from '../../services/auth.service';
import './ChatPage.css';

// ── Helpers ────────────────────────────────────────────────────────────────────
function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatDate(iso) {
  const d     = new Date(iso);
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dMidnight     = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = todayMidnight - dMidnight;

  if (diff === 0)         return 'Today';
  if (diff === 86400000)  return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getInitials(firstName = '', lastName = '') {
  return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
}

function groupMessagesByDate(messages) {
  const groups = [];
  messages.forEach((msg) => {
    const label = formatDate(msg.createdAt);
    const last  = groups[groups.length - 1];
    if (last?.date === label) {
      last.messages.push(msg);
    } else {
      groups.push({ date: label, messages: [msg] });
    }
  });
  return groups;
}

// ── Avatar ─────────────────────────────────────────────────────────────────────
function Avatar({ user, size = 40, online = false }) {
  return (
    <div className="avatar-wrapper" style={{ width: size, height: size }}>
      {user?.avatar ? (
        <img
          src={user.avatar}
          alt={`${user.firstName} ${user.lastName}`}
          className="avatar-img"
          style={{ width: size, height: size }}
        />
      ) : (
        <div
          className="avatar-initials"
          style={{ width: size, height: size, fontSize: size * 0.36 }}
        >
          {getInitials(user?.firstName, user?.lastName)}
        </div>
      )}
      {online && <span className="avatar-online-dot" />}
    </div>
  );
}

// ── Conversation List Item ─────────────────────────────────────────────────────
function ConversationItem({ conv, active, online, onClick }) {
  const lastMsg = conv.lastMessage;
  return (
    <div
      className={`conv-item ${active ? 'conv-item--active' : ''}`}
      onClick={onClick}
    >
      <Avatar user={conv.peer} size={46} online={online} />
      <div className="conv-item__body">
        <div className="conv-item__top">
          <span className="conv-item__name">
            {conv.peer?.firstName} {conv.peer?.lastName}
          </span>
          {lastMsg && (
            <span className="conv-item__time">{formatTime(conv.lastActivityAt)}</span>
          )}
        </div>
        <div className="conv-item__bottom">
          <span className="conv-item__preview">
            {lastMsg ? lastMsg.content : 'Start a conversation'}
          </span>
          {conv.unreadCount > 0 && (
            <span className="conv-item__badge">{conv.unreadCount}</span>
          )}
        </div>
        <span className="conv-item__role">{conv.peer?.role?.displayName}</span>
      </div>
    </div>
  );
}

// ── Message Bubble ──────────────────────────────────────────────────────────────
function MessageBubble({ msg, isMine, showAvatar }) {
  return (
    <div className={`bubble-row ${isMine ? 'bubble-row--mine' : 'bubble-row--theirs'}`}>
      {!isMine && (
        <div className="bubble-avatar-slot">
          {showAvatar && <Avatar user={msg.sender} size={30} />}
        </div>
      )}
      <div className={`bubble ${isMine ? 'bubble--mine' : 'bubble--theirs'}`}>
        <p className="bubble__text">{msg.content}</p>
        <div className="bubble__meta">
          <span className="bubble__time">{formatTime(msg.createdAt)}</span>
          {isMine && (
            <span className={`bubble__tick ${msg.isRead ? 'bubble__tick--read' : ''}`}>
              {msg.isRead ? '✓✓' : '✓'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Typing Indicator ────────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="typing-indicator">
      <div className="typing-dots">
        <span /><span /><span />
      </div>
    </div>
  );
}

// ── New Chat Modal ───────────────────────────────────────────────────────────────
function NewChatModal({ users, onSelect, onClose }) {
  const [search, setSearch] = useState('');

  const filtered = users.filter((u) =>
    `${u.firstName} ${u.lastName} ${u.employeeCode || ''}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h4 className="modal__title">New Message</h4>
          <button className="modal__close" onClick={onClose}>×</button>
        </div>
        <div className="modal__search">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or employee code…"
            className="modal__input"
          />
        </div>
        <div className="modal__list">
          {filtered.length === 0 && (
            <p className="modal__empty">No users found</p>
          )}
          {filtered.map((u) => (
            <div
              key={u.id}
              className="modal__user"
              onClick={() => onSelect(u.id)}
            >
              <Avatar user={u} size={42} />
              <div className="modal__user-info">
                <span className="modal__user-name">
                  {u.firstName} {u.lastName}
                </span>
                <span className="modal__user-meta">
                  {u.role?.displayName} · {u.employeeCode}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Empty / No Selection State ──────────────────────────────────────────────────
function EmptyState({ onNew }) {
  return (
    <div className="empty-state">
      <div className="empty-state__icon">💬</div>
      <h3 className="empty-state__title">Your Messages</h3>
      <p className="empty-state__subtitle">Send messages to your team members</p>
      <button className="btn-primary" onClick={onNew}>
        Start a conversation
      </button>
    </div>
  );
}

// ── Main ChatPage ────────────────────────────────────────────────────────────────
export default function ChatPage() {
  // Get current user from your existing auth.service.js
  const currentUser = authService.getCurrentUser();
  const currentUserId = currentUser?.id;

  const {
    conversations, messages, chatableUsers,
    activeConvId, typingUsers, onlineUsers,
    loadingMsgs, loadingConvs, hasMore,
    selectConversation, startConversation,
    sendMessage, handleTyping, loadMore,
  } = useChat(currentUserId);

  const [input, setInput]         = useState('');
  const [showModal, setShowModal] = useState(false);
  const [searchConv, setSearchConv] = useState('');

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
    inputRef.current?.focus();
  }, [input, sendMessage]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const activePeer    = conversations.find((c) => c.id === activeConvId)?.peer;
  const isPeerOnline  = activePeer ? onlineUsers.has(activePeer.id) : false;
  const isTyping      = typingUsers.size > 0;
  const groupedMsgs   = groupMessagesByDate(messages);

  const filteredConvs = conversations.filter((c) =>
    `${c.peer?.firstName} ${c.peer?.lastName}`
      .toLowerCase()
      .includes(searchConv.toLowerCase())
  );

  return (
    <div className="chat-page">

      {/* ── Sidebar ── */}
      <aside className="chat-sidebar">
        <div className="chat-sidebar__header">
          <h4 className="chat-sidebar__title">Messages</h4>
          <button
            className="btn-new-chat"
            onClick={() => setShowModal(true)}
            title="New message"
          >
            +
          </button>
        </div>

        <div className="chat-sidebar__search">
          <input
            type="text"
            value={searchConv}
            onChange={(e) => setSearchConv(e.target.value)}
            placeholder="Search conversations…"
            className="search-input"
          />
        </div>

        <div className="chat-sidebar__list">
          {loadingConvs && (
            <p className="sidebar-loading">Loading…</p>
          )}
          {!loadingConvs && filteredConvs.length === 0 && (
            <p className="sidebar-empty">No conversations yet</p>
          )}
          {filteredConvs.map((conv) => (
            <ConversationItem
              key={conv.id}
              conv={conv}
              active={conv.id === activeConvId}
              online={onlineUsers.has(conv.peer?.id)}
              onClick={() => selectConversation(conv.id)}
            />
          ))}
        </div>
      </aside>

      {/* ── Chat Area ── */}
      {!activeConvId ? (
        <EmptyState onNew={() => setShowModal(true)} />
      ) : (
        <main className="chat-main">

          {/* Header */}
          {activePeer && (
            <div className="chat-header">
              <Avatar user={activePeer} size={44} online={isPeerOnline} />
              <div className="chat-header__info">
                <span className="chat-header__name">
                  {activePeer.firstName} {activePeer.lastName}
                </span>
                <span className={`chat-header__status ${isPeerOnline ? 'chat-header__status--online' : ''}`}>
                  {isTyping
                    ? 'typing…'
                    : isPeerOnline
                    ? 'Online'
                    : activePeer.role?.displayName}
                </span>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="chat-messages">
            {hasMore && (
              <div className="load-more">
                <button
                  className="btn-load-more"
                  onClick={loadMore}
                  disabled={loadingMsgs}
                >
                  {loadingMsgs ? 'Loading…' : 'Load older messages'}
                </button>
              </div>
            )}

            {groupedMsgs.map((group) => (
              <div key={group.date}>
                {/* Date divider */}
                <div className="date-divider">
                  <div className="date-divider__line" />
                  <span className="date-divider__label">{group.date}</span>
                  <div className="date-divider__line" />
                </div>

                {group.messages.map((msg, i) => {
                  const isMine     = msg.senderId === currentUserId;
                  const prevSender = group.messages[i - 1]?.senderId;
                  const showAvatar = !isMine && prevSender !== msg.senderId;
                  return (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      isMine={isMine}
                      showAvatar={showAvatar}
                    />
                  );
                })}
              </div>
            ))}

            {isTyping && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          {/* Input Bar */}
          <div className="chat-input-bar">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                handleTyping();
              }}
              onKeyDown={handleKeyDown}
              placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
              className="chat-input"
              rows={1}
            />
            <button
              className={`btn-send ${!input.trim() ? 'btn-send--disabled' : ''}`}
              onClick={handleSend}
              disabled={!input.trim()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </main>
      )}

      {/* New Chat Modal */}
      {showModal && (
        <NewChatModal
          users={chatableUsers}
          onSelect={async (peerId) => {
            setShowModal(false);
            await startConversation(peerId);
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}